export const MAIL_SIGNAL_MANAGEMENT_CODE = "901";
export const MAIL_SIGNAL_MANAGEMENT_DESCRIPTION = "Materiale non conforme accettato in deroga";

export function createMailDraft(message = {}) {
    const analysis = message.analysis || {};
    const customer = message.customer_context?.customer || {};
    const attachments = Array.isArray(message.attachments) ? message.attachments : [];
    const suggestedArticles = Array.isArray(analysis.articles) ? analysis.articles : [];
    const base = {
        uid: String(message.uid || ""),
        messageId: message.message_id || "",
        conversationKey: message.conversation?.key || "",
        subject: message.subject || "",
        senderEmail: message.sender?.email || "",
        contactEmail: customer.contact_email || message.sender?.email || "",
        customerCode: customer.code || "",
        customerName: customer.name || "",
        customerConfirmed: customer.confirmed === true,
        addressCode: "",
        customerReference: "",
        articles: suggestedArticles.map((article) => ({
            code: article.code,
            description: article.description || "",
            problemText: article.problem_text || message.body_text || "",
            attachmentIds: [...(article.attachment_ids || [])],
            quantity: null,
            unit: "PZ",
            managementCode: MAIL_SIGNAL_MANAGEMENT_CODE,
            suggestedQuantity: article.suggested_quantity || "",
            suggestedManagement: article.suggested_management || null,
            issueCategories: [...(article.issue_categories || [])],
            confidence: article.confidence || "manuale",
            evidence: [...(article.evidence || [])],
            customerReferences: [...(article.customer_references || [])],
            deliveredQuantity: article.delivered_quantity || "",
            deliveredSince: article.delivered_since || "",
            lastDelivery: article.last_delivery || null,
            deliveries: [...(article.deliveries || [])],
        })),
        unassignedAttachmentIds: [...(analysis.unassigned_attachment_ids || [])],
        eightDEvidenceIds: [],
        attachments,
        assignedTo: message.mail_draft?.assigned_to?.id || null,
        slaDueAt: message.mail_draft?.sla_due_at || null,
        serverUpdatedAt: message.mail_draft?.updated_at || null,
    };
    const serverDraft = message.mail_draft?.draft;
    if (!serverDraft || typeof serverDraft !== "object") return base;
    const restored = {
        ...base,
        ...serverDraft,
        uid: base.uid,
        messageId: base.messageId,
        conversationKey: base.conversationKey,
        attachments: base.attachments,
        assignedTo: message.mail_draft?.assigned_to?.id ?? serverDraft.assignedTo ?? null,
        slaDueAt: message.mail_draft?.sla_due_at ?? serverDraft.slaDueAt ?? null,
        serverUpdatedAt: message.mail_draft?.updated_at || null,
    };
    return {
        ...restored,
        articles: (restored.articles || []).map((article) => ({
            ...article,
            managementCode: MAIL_SIGNAL_MANAGEMENT_CODE,
        })),
    };
}

export function buildServerDraftPayload(message, draft) {
    const persistedDraft = {
        contactEmail: draft.contactEmail || "",
        customerCode: draft.customerCode || "",
        customerName: draft.customerName || "",
        customerConfirmed: draft.customerConfirmed === true,
        addressCode: draft.addressCode || "",
        customerReference: draft.customerReference || "",
        articles: draft.articles || [],
        unassignedAttachmentIds: draft.unassignedAttachmentIds || [],
        eightDEvidenceIds: draft.eightDEvidenceIds || [],
    };
    return {
        uid: String(draft.uid || message?.uid || ""),
        message_id: draft.messageId || message?.message_id || "",
        conversation_key: draft.conversationKey || message?.conversation?.key || "",
        draft: persistedDraft,
        assigned_to: draft.assignedTo || null,
        sla_due_at: draft.slaDueAt || null,
    };
}

export function addArticleToDraft(draft, article, fallbackText = "") {
    if (!draft || !article?.code) return draft;
    if (draft.articles.some((item) => item.code === article.code)) return draft;
    return {
        ...draft,
        articles: [
            ...draft.articles,
            {
                code: article.code,
                description: article.description || "",
                problemText: draft.articles.length ? "" : fallbackText,
                attachmentIds: [],
                quantity: null,
                unit: "PZ",
                managementCode: MAIL_SIGNAL_MANAGEMENT_CODE,
                suggestedQuantity: "",
                suggestedManagement: null,
                issueCategories: [],
                confidence: "manuale",
                evidence: [],
                customerReferences: [...(article.customer_references || [])],
                deliveredQuantity: article.delivered_quantity || "",
                deliveredSince: article.delivered_since || "",
                lastDelivery: article.last_delivery || null,
                deliveries: [...(article.deliveries || [])],
            },
        ],
    };
}

export function buildPreparePayload(message, draft) {
    return {
        uid: draft.uid,
        message_id: draft.messageId,
        subject: draft.subject,
        sender_email: draft.senderEmail,
        customer_code: draft.customerCode,
        customer_name: draft.customerName || "",
        address_code: draft.addressCode,
        customer_reference: draft.customerReference || "",
        articles: draft.articles.map((article) => ({
            code: article.code,
            description: article.description || "",
            problem_text: String(article.problemText || "").trim(),
            attachment_ids: [...(article.attachmentIds || [])],
            quantity: article.quantity || null,
            unit: article.unit || "PZ",
            management_code: MAIL_SIGNAL_MANAGEMENT_CODE,
        })),
        source_preview: message?.snippet || "",
    };
}

export function draftValidation(draft) {
    if (!draft?.customerCode) return "Associa il mittente a un cliente Sage.";
    if (!draft?.customerConfirmed) return "Conferma l’associazione tra contatto e cliente.";
    if (!draft?.addressCode) return "Seleziona l’indirizzo cliente della NC.";
    if (!draft?.articles?.length) return "Associa almeno un articolo.";
    const incomplete = draft.articles.find((article) => !String(article.problemText || "").trim());
    if (incomplete) return `Descrivi il problema per ${incomplete.code}.`;
    const withoutQuantity = draft.articles.find((article) => Number(article.quantity) <= 0);
    if (withoutQuantity) return `Indica la quantità contestata per ${withoutQuantity.code}.`;
    return "";
}

export function applyCustomerToDraft(draft, customer, contactEmail) {
    return {
        ...draft,
        contactEmail: contactEmail || customer?.contact_email || draft?.contactEmail || "",
        customerCode: customer?.code || "",
        customerName: customer?.name || "",
        customerConfirmed: customer?.confirmed === true,
        addressCode: customer?.code === draft?.customerCode ? draft.addressCode : "",
    };
}

export function buildCustomerMappingPayload(customer, contactEmail, contactName = "") {
    return {
        email: String(contactEmail || "").trim().toLowerCase(),
        customer_code: customer?.code || "",
        contact_name: String(contactName || "").trim(),
        confirm_sage_write: true,
    };
}

export function applyAnalysisToDraft(draft, message, analysis) {
    const nextMessage = { ...message, analysis };
    const analyzed = createMailDraft(nextMessage);
    return {
        ...draft,
        articles: analyzed.articles,
        unassignedAttachmentIds: analyzed.unassignedAttachmentIds,
    };
}

export function applyAutomaticSuggestions(draft) {
    if (!draft) return draft;
    return {
        ...draft,
        articles: draft.articles.map((article) => ({
            ...article,
            quantity: article.quantity || article.suggestedQuantity || null,
            managementCode: MAIL_SIGNAL_MANAGEMENT_CODE,
        })),
    };
}

export function createCustomerResponse(message = {}, kind = "acknowledgement") {
    const originalSubject = String(message.subject || "Segnalazione cliente").replace(/^Re:\s*/i, "");
    const is8d = kind === "send_8d";
    return {
        kind,
        subject: `Re: ${originalSubject}${is8d ? " - Rapporto 8D" : ""}`,
        body: is8d
            ? "Buongiorno,\n\nin allegato trasmettiamo il rapporto 8D relativo alla vostra segnalazione.\n\nRestiamo a disposizione per eventuali chiarimenti.\n\nCordiali saluti,\nUfficio Qualità ME.CO.M."
            : "Buongiorno,\n\nconfermiamo la ricezione della vostra segnalazione e la relativa presa in carico da parte del nostro Ufficio Qualità.\n\nSeguirà il rapporto 8D al completamento dell’analisi.\n\nCordiali saluti,\nUfficio Qualità ME.CO.M.",
        attachment: null,
        evidenceAttachmentIds: [],
    };
}

export function customerResponseValidation(response) {
    if (!String(response?.subject || "").trim()) return "Inserire l’oggetto della risposta.";
    if (!String(response?.body || "").trim()) return "Inserire il testo della risposta.";
    if (response?.kind === "send_8d" && !response.attachment) return "Allegare il PDF 8D.";
    if (response?.attachment && !String(response.attachment.name || "").toLowerCase().endsWith(".pdf")) {
        return "L’allegato 8D deve essere un file PDF.";
    }
    if (response?.attachment && Number(response.attachment.size || 0) > 15 * 1024 * 1024) {
        return "Il PDF 8D non può superare 15 MB.";
    }
    return "";
}

export function demoMailMessages() {
    return [
        {
            uid: "demo-1024",
            message_id: "<demo-1024@cliente.example>",
            subject: "Segnalazione staffe MEC-1001 e MEC-2002",
            sender: { name: "Cliente dimostrativo", email: "qualita@cliente.example" },
            recipients: ["quality@mecomsrl.com"],
            copied_recipients: [],
            conversation: { key: "demo-conversation-1024", message_count: 2, is_follow_up: false, linked_to_previous: false },
            mail_draft: {
                assigned_to: { id: 1, name: "Responsabile Qualità" },
                sla_due_at: "2026-09-04T17:00:00+02:00",
                sla_overdue: false,
                updated_at: "2026-09-03T09:10:00+02:00",
                draft: {},
            },
            sent_at: "2026-08-29T09:42:00+02:00",
            snippet: "Durante il montaggio abbiamo riscontrato una piega fuori tolleranza e una graffiatura.",
            body_text:
                "Buongiorno,\n\nSull'articolo MEC-1001 la piega risulta fuori tolleranza.\n\n" +
                "L'articolo MEC-2002 presenta invece una graffiatura sulla superficie a vista.\n\n" +
                "In allegato trovate le fotografie e il rapporto di controllo.",
            attachments: [
                { id: "1", filename: "MEC-1001-piega.jpg", content_type: "image/jpeg", size: 248120, classification: "evidence", hidden_by_default: false, classification_reason: "immagine potenzialmente utile come evidenza" },
                { id: "2", filename: "MEC-2002-graffio.jpg", content_type: "image/jpeg", size: 191840, classification: "evidence", hidden_by_default: false, classification_reason: "immagine potenzialmente utile come evidenza" },
                { id: "3", filename: "rapporto-controllo.pdf", content_type: "application/pdf", size: 88420, classification: "document", hidden_by_default: false, classification_reason: "documento PDF da verificare" },
                { id: "4", filename: "image001.jpg", content_type: "image/jpeg", size: 2840, classification: "signature", hidden_by_default: true, classification_reason: "immagine incorporata di piccole dimensioni; identica in più mail" },
            ],
            customer_context: {
                emails: ["qualita@cliente.example"],
                customer: {
                    code: "C001DEMO",
                    name: "Cliente dimostrativo",
                    contact_email: "qualita@cliente.example",
                    source: "associazione_confermata",
                    confirmed: true,
                },
                candidates: [],
                requires_confirmation: false,
            },
            analysis: {
                articles: [
                    {
                        code: "MEC-1001",
                        description: "Staffa piegata destra",
                        confidence: "alta",
                        problem_text: "Sull'articolo MEC-1001 la piega risulta fuori tolleranza.",
                        attachment_ids: ["1", "3"],
                        evidence: [{ source: "testo mail", matched: "MEC-1001" }],
                        customer_references: ["ST-DESTRA-01"],
                        delivered_quantity: "24",
                        delivered_since: "2026-02-28",
                        last_delivery: {
                            delivery_number: "DDT2601042",
                            date: "2026-08-21",
                            quantity: "24",
                            sales_order: "ODV2601870",
                            sales_order_line: 1000,
                        },
                        deliveries: [
                            {
                                delivery_number: "DDT2601042",
                                date: "2026-08-21",
                                quantity: "24",
                                sales_order: "ODV2601870",
                                sales_order_line: 1000,
                            },
                        ],
                        suggested_quantity: "4",
                        suggested_management: { code: "902", label: "Rifacimento o sostituzione", confidence: "media", reason: "Richiesta di rifacimento rilevata." },
                        issue_categories: ["dimensionale"],
                    },
                    {
                        code: "MEC-2002",
                        description: "Pannello estetico frontale",
                        confidence: "alta",
                        problem_text: "L'articolo MEC-2002 presenta una graffiatura sulla superficie a vista.",
                        attachment_ids: ["2", "3"],
                        evidence: [{ source: "testo mail", matched: "MEC-2002" }],
                        customer_references: ["PF-EST-02"],
                        delivered_quantity: "10",
                        delivered_since: "2026-02-28",
                        last_delivery: {
                            delivery_number: "DDT2600998",
                            date: "2026-08-12",
                            quantity: "10",
                            sales_order: "ODV2601799",
                            sales_order_line: 1000,
                        },
                        deliveries: [
                            {
                                delivery_number: "DDT2600998",
                                date: "2026-08-12",
                                quantity: "10",
                                sales_order: "ODV2601799",
                                sales_order_line: 1000,
                            },
                        ],
                        suggested_quantity: "2",
                        suggested_management: null,
                        issue_categories: ["danno superficiale"],
                    },
                ],
                unassigned_attachment_ids: [],
                requires_manual_photo_review: true,
                recommendation: {
                    action: "create_nc",
                    label: "Proponi creazione NC",
                    confidence: "alta",
                    reason: "Sono presenti indicatori di problema e articoli consegnati riconosciuti.",
                    mail_kind: "new_complaint",
                    summary: "Due articoli con difetti distinti rilevati durante il montaggio.",
                    issue_categories: ["dimensionale", "danno superficiale"],
                    suggested_quantity: "4",
                    suggested_management: { code: "902", label: "Rifacimento o sostituzione" },
                },
            },
            workflow_status: { id: "ready_for_nc", label: "Pronta per NC", color: "blue" },
        },
    ];
}
